import {
  type ColorValue,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  ISettingsColor,
  ISettingsFont,
  ISiteTextPreset,
  IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  memberAreaDesignTitleColor: StyleParamType.Color;
  memberAreaDesignTitleFont: StyleParamType.Font;
  memberAreaDesignDescriptionColor: StyleParamType.Color;
  memberAreaDesignDescriptionFont: StyleParamType.Font;
  memberAreaDesignTextColor: StyleParamType.Color;
  memberAreaDesignTextFont: StyleParamType.Font;
  memberAreaDesignSecondaryTextColor: StyleParamType.Color;
  memberAreaDesignSecondaryTextFont: StyleParamType.Font;
  memberAreaDesignLinksColor: StyleParamType.Color;
  memberAreaDesignLinksFont: StyleParamType.Font;
  memberAreaDesignNoBookingsButtonTextColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonFont: StyleParamType.Font;
  memberAreaDesignNoBookingsButtonColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonBorderColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonBorderWidth: StyleParamType.Number;
  memberAreaDesignNoBookingsButtonBorderRadius: StyleParamType.Number;
  memberAreaDesignSelectedTabBorderColor: StyleParamType.Color;
  memberAreaDesignTabsBorderColor: StyleParamType.Color;
  memberAreaDesignListDividerColor: StyleParamType.Color;
  memberAreaDesignListDividerWidth: StyleParamType.Number;
  memberAreaDesignManageButtonTextColor: StyleParamType.Color;
  memberAreaDesignManageButtonColor: StyleParamType.Color;
  memberAreaDesignManageBorderColor: StyleParamType.Color;
  memberAreaDesignManageFont: StyleParamType.Font;
  memberAreaDesignManageButtonBorderWidth: StyleParamType.Number;
  memberAreaDesignManageButtonBorderRadius: StyleParamType.Number;
  memberAreaDesignBGColorBordersColor: StyleParamType.Color;
  memberAreaDesignBorderColor: StyleParamType.Color;
  memberAreaDesignBorderWidth: StyleParamType.Number;
  memberAreaDesignBorderRadius: StyleParamType.Number;
};

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<PropertyTypeValueMap[K]> extends Required<ValueType> ? K : never]: K
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
    },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
      opacity,
    },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      ...props,
    },
  },
});

export default createStylesParams<IStylesParams>({
  memberAreaDesignTitleColor: colorParam(StyleType.color, 'color-5'),
  memberAreaDesignTitleFont: fontParam('Page-title', { size: 28 }),
  memberAreaDesignDescriptionColor: colorParam(StyleType.color, 'color-5'),
  memberAreaDesignDescriptionFont: fontParam('Body-M', { size: 16 }),
  memberAreaDesignTextColor: colorParam(StyleType.color, 'color-5'),
  memberAreaDesignTextFont: fontParam('Body-M', { size: 16 }),
  memberAreaDesignSecondaryTextColor: colorParam(StyleType.color, 'color-4'),
  memberAreaDesignSecondaryTextFont: fontParam('Body-M', { size: 14 }),
  memberAreaDesignLinksColor: colorParam(StyleType.color, 'color-5'),
  memberAreaDesignLinksFont: fontParam('Body-M', { size: 16 }),
  memberAreaDesignNoBookingsButtonFont: fontParam('Body-M', { size: 16 }),
  memberAreaDesignNoBookingsButtonColor: colorParamWithOpacity(StyleType.color, 'color-8', 1),
  memberAreaDesignNoBookingsButtonTextColor: colorParamWithOpacity(StyleType.color, 'color-1', 1),
  memberAreaDesignNoBookingsButtonBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-8', 1),
  memberAreaDesignNoBookingsButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  memberAreaDesignNoBookingsButtonBorderRadius: numberParam(StyleType.borderRadius, 0),
  memberAreaDesignSelectedTabBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-8', 1),
  memberAreaDesignTabsBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  memberAreaDesignManageButtonTextColor: colorParam(StyleType.color, 'color-8'),
  memberAreaDesignManageButtonColor: colorParamWithOpacity(StyleType.backgroundColor, 'color-1', 1),
  memberAreaDesignManageBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  memberAreaDesignManageFont: fontParam('Body-M', { size: 16 }),
  memberAreaDesignManageButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  memberAreaDesignManageButtonBorderRadius: numberParam(StyleType.borderRadius, 0),
  memberAreaDesignListDividerColor: colorParamWithOpacity(StyleType.color, 'color-5', 0.3),
  memberAreaDesignListDividerWidth: numberParam(StyleType.width, 1),
  memberAreaDesignBGColorBordersColor: colorParamWithOpacity(StyleType.backgroundColor, 'color-1', 1),
  memberAreaDesignBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.3),
  memberAreaDesignBorderWidth: numberParam(StyleType.borderWidth, 0),
  memberAreaDesignBorderRadius: numberParam(StyleType.borderRadius, 0),
});
